import { useState, useMemo } from "react";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { 
  Plus, 
  Calendar as CalendarIcon, 
  ChevronLeft, 
  ChevronRight,
  Users,
  CheckCircle2,
  Clock,
  Plane
} from "lucide-react";
import { useAttendance } from "@/hooks/useAttendance";
import { useAuth } from "@/hooks/useAuth";
import { useQuery } from "@tanstack/react-query";
import { employeeService } from "@/services/employeeService";
import { WorkShift, EmployeeSchedule, Absence, Holiday } from "@/types/attendance";
import { Employee } from "@/types/employee";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogFooter 
} from "@/components/ui/dialog";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import isBetween from "dayjs/plugin/isBetween";
import { toast } from "sonner";

dayjs.extend(isoWeek);
dayjs.extend(isBetween);

export const WeeklyPlanner = () => {
  const { company } = useAuth();
  const slug = company?.slug || "";
  
  const [currentDate, setCurrentDate] = useState(dayjs());
  const [selectedShiftId, setSelectedShiftId] = useState<string>("");
  const [selectedEmployees, setSelectedEmployees] = useState<string[]>([]);
  const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);

  // Week range
  const startOfWeek = useMemo(() => currentDate.startOf("isoWeek"), [currentDate]);
  const endOfWeek = useMemo(() => currentDate.endOf("isoWeek"), [currentDate]);
  const dateRange = useMemo(() => {
    return Array.from({ length: 7 }).map((_, i) => startOfWeek.add(i, "day"));
  }, [startOfWeek]);

  // Data
  const { 
    shifts, 
    schedules, 
    holidays,
    absences,
    isLoadingShifts, 
    isLoadingSchedules, 
    isLoadingHolidays,
    isLoadingAbsences,
    bulkAssignSchedule,
    deleteSchedule
  } = useAttendance(slug, startOfWeek.format("YYYY-MM-DD"), endOfWeek.format("YYYY-MM-DD"));

  const { data: employeesRes, isLoading: isLoadingEmployees } = useQuery({
    queryKey: ["employees", slug],
    queryFn: () => employeeService.getEmployees(slug),
    enabled: !!slug,
  });

  const employees = (employeesRes?.data?.data || []) as Employee[];

  // Helpers
  const getAssignmentForDay = (employeeId: string, date: dayjs.Dayjs) => {
    const dateValue = date.startOf("day");
    return schedules?.find((s: EmployeeSchedule) => 
      s.employee_id === employeeId && 
      !dateValue.isBefore(dayjs(s.start_date), 'day') &&
      !dateValue.isAfter(dayjs(s.end_date), 'day')
    );
  };

  const getAbsenceForDay = (employeeId: string, date: dayjs.Dayjs) => {
    const dateValue = date.startOf("day");
    return absences?.find((a: Absence) => 
      a.employee_id === employeeId && 
      !dateValue.isBefore(dayjs(a.start_date), 'day') &&
      !dateValue.isAfter(dayjs(a.end_date), 'day')
    );
  };

  const getHolidayForDay = (date: dayjs.Dayjs) => {
    const dateStr = date.format("YYYY-MM-DD");
    return holidays?.find((holiday: Holiday) => holiday.date === dateStr);
  };

  const handleBulkAssign = async () => {
    if (!selectedShiftId || selectedEmployees.length === 0) {
      toast.error("Selecciona un turno y al menos un empleado");
      return;
    }

    try {
      await bulkAssignSchedule.mutateAsync({
        employee_ids: selectedEmployees,
        work_shift_id: selectedShiftId,
        start_date: startOfWeek.format("YYYY-MM-DD"),
        end_date: endOfWeek.format("YYYY-MM-DD"),
        slug,
        company_id: company?.id || "",
      });
      setSelectedEmployees([]);
      setIsBulkModalOpen(false);
    } catch (error) {
      // Handled in hook
    }
  };

  const toggleEmployee = (id: string) => {
    setSelectedEmployees(prev => 
      prev.includes(id) ? prev.filter(e => e !== id) : [...prev, id]
    );
  };

  if (isLoadingShifts || isLoadingEmployees || isLoadingHolidays) return <div className="p-8 text-center text-slate-500">Cargando planificador...</div>;

  return (
    <div className="space-y-6">
      {/* Header / Controls */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 bg-white p-4 rounded-xl border border-slate-200/60 shadow-sm">
        <div className="flex items-center gap-4">
          <div className="flex items-center bg-slate-100 rounded-lg p-1">
            <Button variant="ghost" size="icon" onClick={() => setCurrentDate(currentDate.subtract(1, "week"))}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <div className="px-4 py-1 text-sm font-semibold text-slate-700 min-w-[200px] text-center">
              {startOfWeek.format("DD MMM")} - {endOfWeek.format("DD MMM, YYYY")}
            </div>
            <Button variant="ghost" size="icon" onClick={() => setCurrentDate(currentDate.add(1, "week"))}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
          <Button variant="outline" size="sm" onClick={() => setCurrentDate(dayjs())}>Hoy</Button>
        </div>

        <div className="flex items-center gap-2">
          {selectedEmployees.length > 0 && (
            <Button onClick={() => setIsBulkModalOpen(true)} className="gap-2 bg-blue-600 hover:bg-blue-700">
              <Plus className="h-4 w-4" /> Asignar a {selectedEmployees.length} seleccionados
            </Button>
          )}
        </div>
      </div>

      {/* Grid */}
      <div className="border border-slate-200/60 rounded-xl bg-white shadow-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50/50">
              <TableHead className="w-[50px] px-4">
                <Checkbox 
                  checked={selectedEmployees.length === employees.length && employees.length > 0}
                  onCheckedChange={(checked) => {
                    if (checked) setSelectedEmployees(employees.map((employee) => employee.id));
                    else setSelectedEmployees([]);
                  }}
                />
              </TableHead>
              <TableHead className="w-[200px] font-bold text-slate-700">Empleado</TableHead>
              {dateRange.map((date) => (
                <TableHead key={date.format("YYYY-MM-DD")} className="text-center py-4">
                  <div className="flex flex-col items-center">
                    <span className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">
                      {date.format("ddd")}
                    </span>
                    <span className={`h-8 w-8 flex items-center justify-center rounded-full mt-1 text-sm font-bold ${date.isSame(dayjs(), 'day') ? 'bg-blue-600 text-white' : 'text-slate-700'}`}>
                      {date.format("DD")}
                    </span>
                  </div>
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {employees.map((emp: Employee) => (
              <TableRow key={emp.id} className="hover:bg-slate-50/30 transition-colors">
                <TableCell className="px-4">
                  <Checkbox 
                    checked={selectedEmployees.includes(emp.id)}
                    onCheckedChange={() => toggleEmployee(emp.id)}
                  />
                </TableCell>
                <TableCell>
                  <div className="flex flex-col">
                    <span className="font-semibold text-slate-900">{emp.name}</span>
                    <span className="text-[10px] text-slate-400 truncate">{emp.position || emp.area}</span>
                  </div>
                </TableCell>
                {dateRange.map((date) => {
                  const assignment = getAssignmentForDay(emp.id, date);
                  const absence = getAbsenceForDay(emp.id, date);
                  const holiday = getHolidayForDay(date);
                  const isWorkDayInDefault = emp.workShift?.work_days?.includes(date.day());
                  const typeMap = { 'Vacation': 'Vacaciones', 'Permission': 'Permiso', 'Medical': 'Médico' };
                  
                  return (
                    <TableCell key={date.format("YYYY-MM-DD")} className="p-2 border-l border-slate-50 min-h-[80px] w-32 align-top">
                      <div className="flex flex-col gap-1 h-full">
                        {holiday && (
                          <Badge
                            variant="outline"
                            className="w-full justify-start gap-1 py-1 px-1.5 bg-violet-50 text-violet-700 border-violet-200 text-[9px] font-bold"
                            title={holiday.description}
                          >
                            <CalendarIcon className="h-3 w-3" />
                            {holiday.description || 'Festivo'}
                          </Badge>
                        )}

                        {absence && (
                          <Badge 
                            variant="outline" 
                            className="w-full justify-start gap-1 py-1 px-1.5 bg-rose-50 text-rose-700 border-rose-200 text-[9px] font-bold"
                            title={absence.description}
                          >
                            <Plane className="h-3 w-3" />
                            {typeMap[absence.type] || 'Ausente'}
                          </Badge>
                        )}
                        
                        {assignment ? (
                          <div className="group relative">
                            <Badge 
                              variant="secondary" 
                              className={`w-full justify-start gap-1 py-1.5 px-2 transition-colors cursor-default ${absence ? 'bg-amber-50 text-amber-700 border-amber-200 opacity-60' : 'bg-blue-50 text-blue-700 border-blue-100 hover:bg-blue-100'}`}
                            >
                              <Clock className="h-3 w-3" />
                              <span className="truncate flex-1 text-[10px] font-bold">{assignment.workShift?.name}</span>
                            </Badge>
                            <button 
                              onClick={() => deleteSchedule.mutate(assignment.id)}
                              className="absolute -top-1 -right-1 h-4 w-4 bg-rose-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-sm"
                            >
                              ×
                            </button>
                            {!absence && (
                              <div className="mt-1 text-[9px] text-slate-400 font-medium px-1">
                                {assignment.workShift?.start_time?.substring(0, 5)} - {assignment.workShift?.end_time?.substring(0, 5)}
                              </div>
                            )}
                          </div>
                        ) : !absence && !holiday && (
                          <div className="flex flex-col h-full opacity-30 italic">
                            {isWorkDayInDefault ? (
                              <>
                                <div className="flex items-center gap-1 text-[9px] text-slate-500 px-1">
                                  <CheckCircle2 className="h-3 w-3 text-emerald-500" />
                                  {emp.workShift?.name}
                                </div>
                                <span className="text-[8px] px-1">(Base)</span>
                              </>
                            ) : (
                              <span className="text-[9px] text-slate-300 px-1">No lab.</span>
                            )}
                          </div>
                        )}
                      </div>
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Bulk Assign Modal */}
      <Dialog open={isBulkModalOpen} onOpenChange={setIsBulkModalOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-blue-600" />
              Asignación Masiva Semanal
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">Turno a asignar</label>
              <Select onValueChange={setSelectedShiftId} value={selectedShiftId}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecciona un turno" />
                </SelectTrigger>
                <SelectContent>
                  {shifts?.map((shift: WorkShift) => (
                    <SelectItem key={shift.id} value={shift.id}>
                      {shift.name} ({shift.start_time.substring(0, 5)} - {shift.end_time.substring(0, 5)})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="p-3 bg-blue-50 border border-blue-100 rounded-lg text-xs text-blue-800">
              Esta acción asignará el turno seleccionado a **{selectedEmployees.length} empleados** para la semana del **{startOfWeek.format("DD/MM")}** al **{endOfWeek.format("DD/MM")}**.
            </div>
            <div className="p-3 bg-violet-50 border border-violet-100 rounded-lg text-xs text-violet-800">
              Los días festivos se bloquearán automáticamente durante la asignación, igual que las ausencias registradas.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsBulkModalOpen(false)}>Cancelar</Button>
            <Button onClick={handleBulkAssign} disabled={bulkAssignSchedule.isPending}>
              {bulkAssignSchedule.isPending ? "Asignando..." : "Confirmar Asignación"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
